import { Alert, Divider, message, Segmented, Spin, Typography } from 'antd';
import Card from 'components/Card/Card';
import { useEffect, useRef } from 'react';
import { Redirect, Switch, useHistory, useParams, useLocation } from 'react-router-dom/cjs/react-router-dom';
import { useAsync } from 'react-async-hook';
import { familyService } from 'services';
import ProtectedRoute from 'components/ProtectedRoute';
import { Spacer } from 'components/Space/Space';
import { FAMILY_APPLICATION_STEPS, FAMILY_BRANCH_MAP } from './familyApplicationConstants';
import { useAtom, useSetAtom } from 'jotai';
import {
  applicationAtom,
  currentStepAtom,
  featureFlagsAtom,
  stepConfigAtom,
  stepCompletionsAtom,
  getLatestAvailableStep,
  getStepKey,
  CHILD_STEPS_EXCLUDED_FROM_ENTITY_INDEX,
} from './atoms';
import React from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { SCROLLING_LAYOUT_CLASSNAME } from 'components/Layout/Layout';
import dayjs from 'dayjs';
import { DATE_FORMAT_SENTENCE } from 'components/AriaDatePicker/AriaDatePicker';
import { PERMISSION_DENIED_ERROR } from 'services/interceptors';

// Steps - sponsor
import { FamilyApplicationStart } from './__steps/sponsor/FamilyApplicationStart';
import { FamilyApplicationComponent } from './__steps/sponsor/FamilyApplicationComponent';
import { FamilyApplicationStatus } from './__steps/sponsor/FamilyApplicationStatus';
import { FamilyApplicationRank } from './__steps/sponsor/FamilyApplicationRank';
import { FamilyApplicationMaritalStatus } from './__steps/sponsor/FamilyApplicationMaritalStatus';
import { FamilyApplicationCivilianEmployment } from './__steps/sponsor/FamilyApplicationCivilianEmployment';
import { FamilyApplicationContactInfo } from './__steps/sponsor/FamilyApplicationContactInfo';
import { FamilyApplicationNavigation } from './__components/FamilyApplicationNavigation';
import { FamilyApplicationLocation } from './__steps/sponsor/FamilyApplicationLocation';
import { FamilyApplicationDocuments } from './__steps/sponsor/FamilyApplicationDocuments';

// Steps - household
import { FamilyAppHouseholdStructure } from './__steps/household/FamilyAppHouseholdStructure';
import { FamilyAppSpouseResidenceStatus } from './__steps/household/FamilyAppSpouseResidenceStatus';
import FamilyAppEmploymentActivities from './__steps/household/FamilyAppEmploymentActivities';
import FamilyAppSpouseEmploymentType from './__steps/household/FamilyAppSpouseEmploymentType';
import FamilyAppSpouseEmployerDetails from './__steps/household/FamilyAppSpouseEmployerDetails';
import FamilyAppSpouseSelfEmployment from './__steps/household/FamilyAppSpouseSelfEmployment';
import FamilyAppSpouseUnemploymentReason from './__steps/household/FamilyAppSpouseUnemploymentReason';
import { FamilyAppSpouseMilitaryEmployment } from './__steps/household/FamilyAppSpouseMilitaryEmployment';
import FamilyAppSpouseJobSearch from './__steps/household/FamilyAppSpouseJobSearch';
import FamilyAppSpouseEducation from './__steps/household/FamilyAppSpouseEducation';
import FamilyAppEligibilitySummary from './__steps/household/FamilyAppEligibilitySummary';
import { FamilyApplicationSpouseContactInfo } from './__steps/household/FamilyApplicationSpouseContactInfo';
import { FamilyAppSpouseDocumentationRequirements } from './__steps/household/FamilyAppSpouseDocumentationRequirements';

// Steps - children
import { FamilyApplicationChildrenBasicInfo } from './__steps/children/FamilyApplicationChildrenBasicInfo';
import { FamilyApplicationChildrenLivingSituation } from './__steps/children/FamilyApplicationChildrenLivingSituation';
import { FamilyApplicationChildrenEducationalStatus } from './__steps/children/FamilyApplicationChildrenEducationalStatus';
import { FamilyApplicationChildrenChildCareNeeds } from './__steps/children/FamilyApplicationChildrenChildCareNeeds';
import { FamilyApplicationChildrenCareNeedType } from './__steps/children/FamilyApplicationChildrenCareNeedType';
import { FamilyApplicationChildrenScheduleOfCare } from './__steps/children/FamilyApplicationChildrenScheduleOfCare';
import { FamilyApplicationChildrenDailyTimeBlocks } from './__steps/children/FamilyApplicationChildrenDailyTimeBlocks';
import { FamilyApplicationChildrenProviderConnection } from './__steps/children/FamilyApplicationChildrenProviderConnection';
import { FamilyApplicationChildrenProviderInfo } from './__steps/children/FamilyApplicationChildrenProviderInfo';
import { FamilyApplicationChildrenSummary } from './__steps/children/FamilyApplicationChildrenSummary';
import { FamilyApplicationChildrenEligibilityCertification } from './__steps/children/FamilyApplicationChildrenEligibilityCertification';
import { FamilyApplicationChildrenDocuments } from './__steps/children/FamilyApplicationChildrenDocuments';

// Documentation Requirements
import { FamilyApplicationDocumentationRequirements } from './__steps/documentation/FamilyApplicationDocumentationRequirements';

// Agreement
import { FamilyApplicationAgreement } from './__steps/agreement/FamilyApplicationAgreement';

// Submitted
import { FamilyApplicationSubmitted } from './__steps/submitted/FamilyApplicationSubmitted';
import { FamilyApplicationSubmittedDeprecated } from './__steps/submitted/FamilyApplicationSubmitted_Deprecated';

export const ADD_APPLICATION_VIEW_MAP = {
  FAMILY: 'family',
  REVIEW: 'review',
};

export function AddApplication2({
  applicationView = ADD_APPLICATION_VIEW_MAP.FAMILY,
  baseRoute = '/family/applications',
  readOnly = false,
}) {
  const history = useHistory();
  const location = useLocation();
  const params = useParams();
  const { id: applicationId } = params;
  const { showFamilyApplicationViewAsSwitcher, showFamilyApplicationChecklist, showFamilyApplicationsScheduleOfCare } =
    useFlags();

  const setFeatureFlags = useSetAtom(featureFlagsAtom);
  useEffect(() => {
    setFeatureFlags({ showFamilyApplicationsScheduleOfCare });
  }, [showFamilyApplicationsScheduleOfCare, setFeatureFlags]);

  // Extract step from URL - get what comes after /applications/{applicationId}/
  const basePath = `${baseRoute}/${applicationId}`;
  const stepWithQuery = location.pathname.startsWith(basePath + '/')
    ? location.pathname.substring(basePath.length + 1) + location.search
    : null;
  const step = stepWithQuery ? stepWithQuery.split('?')[0] : null;

  // Extract entity index for children steps
  const urlParams = new URLSearchParams(location.search);
  const entityIndex = urlParams.get('entity') ? parseInt(urlParams.get('entity')) : null;

  const isNewApplication = applicationId === 'new';

  const [application, setApplication] = useAtom(applicationAtom);
  const [currentStep, setCurrentStep] = useAtom(currentStepAtom);
  const [stepConfig] = useAtom(stepConfigAtom);
  const [stepCompletions, setStepCompletions] = useAtom(stepCompletionsAtom);
  const previousStepRef = useRef(null);
  const previousEntityIndexRef = useRef(null);
  const initializedRef = useRef(false);

  const { result: household = [] } = useAsync(async () => {
    if (applicationView === 'family') {
      return familyService.getHouseHold();
    }

    return {
      id: params.householdId,
    };
  }, [applicationView, params.householdId]);

  // Fetch application details if not a new application
  useAsync(async () => {
    if (!isNewApplication && !application) {
      try {
        const _application = await familyService.getApplication(applicationId);
        setApplication(_application);
      } catch (e) {
        if (e.message === PERMISSION_DENIED_ERROR) {
          history.push(baseRoute);
          return;
        }

        console.log('Error fetching application', e);
        message.error('Error fetching application');
      }
    }
  }, [application, isNewApplication]);

  // Update current step when URL changes and capture previous step
  useEffect(() => {
    if (step && step !== currentStep) {
      // Capture the current step as previous before updating
      if (currentStep) {
        previousStepRef.current = currentStep;
        // Also capture the entity index from the previous URL
        const prevUrlParams = new URLSearchParams(location.search);
        previousEntityIndexRef.current = prevUrlParams.get('entity') ? parseInt(prevUrlParams.get('entity')) : null;
      }
      setCurrentStep(step);
    }
  }, [setCurrentStep, step, currentStep, location.search]);

  // Redirect to latest available step if no step is specified
  useEffect(() => {
    if (!isNewApplication && application && !step && stepCompletions !== null && stepConfig !== null) {
      const latestStep = getLatestAvailableStep(application, stepConfig);

      if (latestStep) {
        history.push(`${baseRoute}/${applicationId}/${latestStep}`);
      }
    }
  }, [application, step, isNewApplication, stepConfig, history, applicationId, baseRoute, stepCompletions]);

  // Load step completions on mount and initialize if needed
  useEffect(() => {
    // Reset initialization flag when application changes
    initializedRef.current = false;

    const loadStepCompletions = async () => {
      if (!application?.id || initializedRef.current) return;

      try {
        let completions = await familyService.getStepCompletions(application.id);

        // If no completions exist in backend, initialize with current step statuses
        if (!completions || completions.length === 0) {
          const initialCompletions = [];

          // Get all applicable steps and their current completion status
          Object.keys(stepConfig).forEach((stepId) => {
            const config = stepConfig?.[stepId];

            // Handle children steps separately - need to check for each child
            if (stepId.startsWith('children-') && !CHILD_STEPS_EXCLUDED_FROM_ENTITY_INDEX.includes(stepId)) {
              // Handle per-child steps
              const children = application?.children || [];
              children.forEach((child, childIndex) => {
                // Check if step applies to this child
                const doesApply =
                  config.doesApply === true ||
                  (typeof config.doesApply === 'function' ? config.doesApply(application, childIndex) : false);

                if (doesApply) {
                  // Get current completion status for this child
                  const isCompleted =
                    typeof config.isCompleted === 'function' ? config.isCompleted(application, childIndex) : false;

                  initialCompletions.push({
                    stepKey: getStepKey(stepId, childIndex),
                    completed: isCompleted,
                  });
                }
              });
            } else {
              // Regular (non-children) steps
              const doesApply =
                config.doesApply === true ||
                (typeof config.doesApply === 'function' ? config.doesApply(application) : false);

              if (doesApply) {
                // Get current completion status
                const isCompleted = typeof config.isCompleted === 'function' ? config.isCompleted(application) : false;

                initialCompletions.push({
                  stepKey: stepId,
                  completed: isCompleted,
                });
              }
            }
          });

          // Create the initial completions in the backend
          if (initialCompletions.length > 0) {
            completions = await familyService.createStepCompletion(application.id, initialCompletions);
          } else {
            completions = [];
          }
        }

        setStepCompletions(completions || []);
        initializedRef.current = true;
      } catch (error) {
        console.error('Failed to load step completions:', error);
        setStepCompletions([]);
        initializedRef.current = true;
      }
    };

    loadStepCompletions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application?.id]);

  // Track step completion changes for both informational and data-based steps
  useEffect(() => {
    if (stepCompletions === null || !application?.id || !currentStep) {
      return;
    }

    // Only process if we actually have a previous step and it's different from current
    if (!previousStepRef.current || previousStepRef.current === currentStep) {
      previousStepRef.current = currentStep;
      return;
    }

    const trackStepCompletion = async () => {
      const previousStepConfig = stepConfig[previousStepRef.current];
      const previousStepKey = getStepKey(previousStepRef.current, previousEntityIndexRef.current);
      const existingCompletion = stepCompletions?.find((s) => s.stepKey === previousStepKey);

      if (previousStepConfig?.isInformational) {
        // For informational steps, always mark as completed when navigating away
        if (existingCompletion && !existingCompletion.completed) {
          try {
            await familyService.updateStepCompletion(application.id, existingCompletion.id, previousStepKey, true);

            setStepCompletions((prev) =>
              prev.map((s) => (s.stepKey === previousStepKey ? { ...s, completed: true } : s)),
            );
          } catch (error) {
            console.error('Failed to mark informational step complete:', error);
          }
        } else if (!existingCompletion) {
          // Step is missing from completions, add it as completed
          try {
            const newCompletion = { stepKey: previousStepKey, completed: true };

            const newSavedCompletion = await familyService.createStepCompletion(application.id, [newCompletion]);
            setStepCompletions((prev) => [...prev, ...newSavedCompletion]);
          } catch (error) {
            console.error('Failed to add missing informational step completion:', error);
          }
        }
      } else if (previousStepConfig) {
        // For data-based steps, update based on actual completion status
        const isCompleted = previousStepConfig.isCompleted?.(application, previousEntityIndexRef.current) || false;

        if (existingCompletion && isCompleted !== existingCompletion.completed) {
          try {
            await familyService.updateStepCompletion(
              application.id,
              existingCompletion.id,
              previousStepKey,
              isCompleted,
            );

            setStepCompletions((prev) =>
              prev.map((s) => (s.stepKey === previousStepKey ? { ...s, completed: isCompleted } : s)),
            );
          } catch (error) {
            console.error('Failed to update data step completion:', error);
          }
        } else if (!existingCompletion) {
          // Step is missing from completions, add it with current status
          try {
            const newCompletion = { stepKey: previousStepKey, completed: isCompleted };
            const newSavedCompletion = await familyService.createStepCompletion(application.id, [newCompletion]);
            setStepCompletions((prev) => [...prev, ...newSavedCompletion]);
          } catch (error) {
            console.error('Failed to add missing data step completion:', error);
          }
        }
      }

      // Update the previous step ref at the end
      previousStepRef.current = currentStep;
      // Also update the entity index ref
      previousEntityIndexRef.current = entityIndex;
    };

    trackStepCompletion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const commonProps = {
    // APPLICATION
    application,
    setApplication,
    applicationIsSubmitted: !!application?.submittedDate,
    readOnly: !!application?.submittedDate || readOnly,
    household,
    loading: !isNewApplication && (application === null || stepConfig === null || stepCompletions === null),
    baseRoute,
    applicationView,
    // STEP
    step,
    setStep: (_configs) => {
      if (isNewApplication) {
        // First replace the current page with the start step
        history.push(`${baseRoute}/${_configs?.applicationId || applicationId}/${FAMILY_APPLICATION_STEPS.START}`, {
          replace: true,
        });

        // This needs to be in a setTimeout to ensure proper sequence
        setTimeout(() => {
          history.push(`${baseRoute}/${_configs?.applicationId || applicationId}/${_configs?.newStep}`);
        }, 0);
      } else {
        history.push(`${baseRoute}/${_configs?.applicationId || applicationId}/${_configs?.newStep}`);
      }
    },
  };

  useEffect(() => {
    // Scroll to top on step change
    setTimeout(() => {
      document?.querySelector(`div.${SCROLLING_LAYOUT_CLASSNAME}`)?.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    });
  }, [step]);

  // Clear atom when component unmounts (user leaves AddApplication routes)
  useEffect(() => {
    return () => {
      setApplication(null);
    };
  }, [setApplication]);

  if (!isNewApplication && isNaN(parseInt(applicationId))) {
    return <Redirect to={baseRoute} />;
  }

  // Off-ramp non-Army incomplete applications
  const applicantBranchId = application?.applicant?.militaryDetails?.branch?.id;
  if (
    application &&
    !application.submittedDate &&
    typeof applicantBranchId === 'number' &&
    applicantBranchId !== FAMILY_BRANCH_MAP.army.id
  ) {
    return <Redirect to="/family/transition" />;
  }

  // Show loading spinner if application hasn't loaded yet
  if (!isNewApplication && commonProps.loading) {
    return (
      <div className="h-full flex flex-col items-center justify-around">
        <Spin className="table m-auto" spinning />
      </div>
    );
  }

  // If on submitted step, render without navigation (except in REVIEW mode)
  if (
    currentStep === FAMILY_APPLICATION_STEPS.SUBMITTED &&
    !isNewApplication &&
    applicationView === ADD_APPLICATION_VIEW_MAP.FAMILY
  ) {
    return (
      <div className="max-w-[1024px] mx-auto">
        <Switch>
          <ProtectedRoute
            exact
            path={`${baseRoute}/:id/submitted`}
            message="Navigated to family application submitted page"
          >
            {showFamilyApplicationChecklist ? (
              <FamilyApplicationSubmitted {...commonProps} />
            ) : (
              <FamilyApplicationSubmittedDeprecated {...commonProps} />
            )}
          </ProtectedRoute>
        </Switch>
      </div>
    );
  }

  return (
    <div className="max-w-[1024px] mx-auto">
      <Card lightInMobile noBodyPadding className="overflow-hidden">
        <div className="bg-[#1e2a90] bg-gradient-to-tr from-[#1e2a90] to-[#121952] text-white p-8 flex gap-8 items-center">
          <div>
            {applicationView === ADD_APPLICATION_VIEW_MAP.FAMILY && (
              <Typography.Title level={1} className="!text-white !m-0 !text-3xl !font-bold">
                Military Family Assistance
              </Typography.Title>
            )}

            {isNewApplication ? (
              <p className="text-[#b9c1ff]">New Family Application</p>
            ) : (
              <div className="flex items-center gap-4 text-[#b9c1ff]">
                <span>
                  Status:{' '}
                  {application?.submittedDate
                    ? `Submitted on ${dayjs(application?.submittedDate).format(DATE_FORMAT_SENTENCE)}`
                    : 'In Progress'}
                </span>
              </div>
            )}
          </div>
        </div>

        {!isNewApplication && (
          <FamilyApplicationNavigation
            application={application}
            isNewApplication={isNewApplication}
            step={step}
            stepConfig={stepConfig}
            baseRoute={baseRoute}
          />
        )}
      </Card>

      {applicationView === ADD_APPLICATION_VIEW_MAP.FAMILY &&
        application?.completedBySponsor !== undefined &&
        showFamilyApplicationViewAsSwitcher && (
          <Card className="overflow-hidden mt-8 mx-8 md:mx-0">
            <div className="flex gap-4 items-center flex-col md:flex-row">
              <Spacer />

              <Typography.Text className="!m-0 font-semibold">View as</Typography.Text>

              <Segmented
                options={[
                  {
                    label: 'Service Member',
                    value: true,
                  },
                  {
                    label: 'On behalf',
                    value: false,
                  },
                ]}
                value={application?.completedBySponsor}
                onChange={() => {
                  setApplication((prev) => ({
                    ...prev,
                    completedBySponsor: !prev?.completedBySponsor,
                  }));
                }}
              />
            </div>
          </Card>
        )}

      {applicationView === ADD_APPLICATION_VIEW_MAP.REVIEW && <Divider />}

      {applicationView === ADD_APPLICATION_VIEW_MAP.FAMILY &&
        application?.submittedDate &&
        currentStep !== FAMILY_APPLICATION_STEPS.SUBMITTED && (
          <div className="mx-7 md:mx-0 my-7">
            <Alert
              message={<p className="font-bold">This application is no longer editable</p>}
              description={
                <>
                  It was{' '}
                  <span className="font-semibold">
                    submitted on {dayjs(application?.submittedDate).format(DATE_FORMAT_SENTENCE)},{' '}
                  </span>
                  and you can no longer make changes.
                </>
              }
              type="success"
              showIcon
              className="!border !border-solid !border-green-500 !border-opacity-40"
            />
          </div>
        )}

      <Card lightInMobile noBodyPadding className="mt-8">
        {isNewApplication ? (
          <FamilyApplicationStart {...commonProps} />
        ) : commonProps.loading ? (
          <div className="p-8">
            <Spin className="table m-auto" />
          </div>
        ) : (
          <Switch>
            {/*
             * Steps - sponsor
             */}
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/start`}
              message="Navigated to family application getting started page"
            >
              <FamilyApplicationStart {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/component`}
              message="Navigated to family application component page"
            >
              <FamilyApplicationComponent {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/status`}
              message="Navigated to family application status page"
            >
              <FamilyApplicationStatus {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute exact path={`${baseRoute}/:id/rank`} message="Navigated to family application rank page">
              <FamilyApplicationRank {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/marital-status`}
              message="Navigated to family application marital status page"
            >
              <FamilyApplicationMaritalStatus {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/civilian-employment`}
              message="Navigated to family application civilian employment page"
            >
              <FamilyApplicationCivilianEmployment {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/location`}
              message="Navigated to family application location page"
            >
              <FamilyApplicationLocation {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/contact`}
              message="Navigated to family application contact page"
            >
              <FamilyApplicationContactInfo {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/documents`}
              message="Navigated to family application documents page"
            >
              <FamilyApplicationDocuments {...commonProps} />
            </ProtectedRoute>

            {/*
             * Steps - household
             */}
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/household-structure`}
              message="Navigated to family application household structure page"
            >
              <FamilyAppHouseholdStructure {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/spouse-residence-status`}
              message="Navigated to family application spouse residence status page"
            >
              <FamilyAppSpouseResidenceStatus {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/employment-activities`}
              message="Navigated to family application employment activities page"
            >
              <FamilyAppEmploymentActivities {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/spouse-employment-type`}
              message="Navigated to family application spouse employment type page"
            >
              <FamilyAppSpouseEmploymentType {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/spouse-employer-details`}
              message="Navigated to family application spouse employer details page"
            >
              <FamilyAppSpouseEmployerDetails {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/spouse-self-employment`}
              message="Navigated to family application spouse self-employment page"
            >
              <FamilyAppSpouseSelfEmployment {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/spouse-military-employment`}
              message="Navigated to family application spouse military employment page"
            >
              <FamilyAppSpouseMilitaryEmployment {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/spouse-job-search`}
              message="Navigated to family application spouse job search page"
            >
              <FamilyAppSpouseJobSearch {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/spouse-unemployment-reason`}
              message="Navigated to family application spouse unemployment reason page"
            >
              <FamilyAppSpouseUnemploymentReason {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/spouse-education`}
              message="Navigated to family application spouse education page"
            >
              <FamilyAppSpouseEducation {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/eligibility-summary`}
              message="Navigated to family application eligibility summary page"
            >
              <FamilyAppEligibilitySummary {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/spouse-contact-info`}
              message="Navigated to family application spouse contact info page"
            >
              <FamilyApplicationSpouseContactInfo {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/spouse-documentation-requirements`}
              message="Navigated to family application spouse documentation requirements page"
            >
              <FamilyAppSpouseDocumentationRequirements {...commonProps} />
            </ProtectedRoute>

            {/*
             * Steps - children
             */}
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/children-basic-info`}
              message="Navigated to family application children basic info page"
            >
              <FamilyApplicationChildrenBasicInfo {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/children-living-situation`}
              message="Navigated to family application children living situation page"
            >
              <FamilyApplicationChildrenLivingSituation {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/children-educational-status`}
              message="Navigated to family application children educational status page"
            >
              <FamilyApplicationChildrenEducationalStatus {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/children-child-care-needs`}
              message="Navigated to family application children child care needs page"
            >
              <FamilyApplicationChildrenChildCareNeeds {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/children-care-need-type`}
              message="Navigated to family application children care need type page"
            >
              <FamilyApplicationChildrenCareNeedType {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/children-schedule-of-care`}
              message="Navigated to family application children schedule of care page"
            >
              <FamilyApplicationChildrenScheduleOfCare {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/children-daily-time-blocks`}
              message="Navigated to family application children daily time blocks page"
            >
              <FamilyApplicationChildrenDailyTimeBlocks {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/children-provider-connection`}
              message="Navigated to family application children provider connection page"
            >
              <FamilyApplicationChildrenProviderConnection {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/children-provider-info`}
              message="Navigated to family application children provider info page"
            >
              <FamilyApplicationChildrenProviderInfo {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/children-summary`}
              message="Navigated to family application children summary page"
            >
              <FamilyApplicationChildrenSummary {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/children-eligibility-certification`}
              message="Navigated to family application children eligibility certification page"
            >
              <FamilyApplicationChildrenEligibilityCertification {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/children-documents`}
              message="Navigated to family application children documents page"
            >
              <FamilyApplicationChildrenDocuments {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/documentation-requirements`}
              message="Navigated to family application documentation requirements page"
            >
              <FamilyApplicationDocumentationRequirements {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/agreement`}
              message="Navigated to family application agreement page"
            >
              <FamilyApplicationAgreement {...commonProps} />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={`${baseRoute}/:id/submitted`}
              message="Navigated to family application submitted page"
            >
              {showFamilyApplicationChecklist ? (
                <FamilyApplicationSubmitted {...commonProps} />
              ) : (
                <FamilyApplicationSubmittedDeprecated {...commonProps} />
              )}
            </ProtectedRoute>
          </Switch>
        )}
      </Card>
    </div>
  );
}
